/**
 * Reviewing what the scan found.
 *
 * Two findings, two tones, and the difference is the whole design.
 *
 * **Copies** are a chore: the same photograph twice, nothing to decide, tick
 * the extras and move on. They are ticked in advance.
 *
 * **Moments** are a choice: six group selfies taken seconds apart, everybody
 * pulling a different face, and in exactly one of them nobody blinked. Nothing
 * here is ticked, ever, by anything other than a person — the whole value is in
 * *looking*, which is why this side of the screen has a compare view instead of
 * a delete button pointed at it.
 *
 * The rule underneath both: nothing is deleted that was not explicitly chosen,
 * and what does happen is a move to the trash. It is an algorithm making a
 * suggestion about somebody's photographs, and it is allowed to be wrong.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../components/ui/Button.js";
import { useDecryptedUrl } from "../../lib/useDecryptedUrl.js";
import type { MediaRef } from "../../lib/useDecryptedUrl.js";
import { IDENTICAL_WITHIN, type RelatedSet, reclaimable } from "./phash.js";
import { type ScannableItem, useDuplicateScan } from "./useDuplicateScan.js";
import { CompareView } from "./CompareView.js";
import { loadReviewed, saveReviewed, setSignature } from "./reviewed.js";
import "./DuplicatesPanel.css";

export interface DuplicatesPanelProps {
  groupId: string;
  items: ScannableItem[];
  thumbRefs: ReadonlyMap<string, MediaRef>;
  /** Pulls every remaining page, so the scan sees the whole library. */
  loadEverything: () => Promise<void>;
  canDelete: boolean;
  onDelete: (fileIds: string[]) => void;
  onClose: () => void;
}

export function DuplicatesPanel({
  groupId,
  items,
  thumbRefs,
  loadEverything,
  canDelete,
  onDelete,
  onClose,
}: DuplicatesPanelProps) {
  const { state, scan, reset } = useDuplicateScan(groupId);
  const [marked, setMarked] = useState<ReadonlySet<string>>(new Set());
  const [comparing, setComparing] = useState<RelatedSet | null>(null);
  const [reviewed, setReviewed] = useState<ReadonlySet<string>>(() =>
    loadReviewed(groupId),
  );
  const [showReviewed, setShowReviewed] = useState(false);

  useEffect(() => {
    void scan(items, thumbRefs, loadEverything);
    return reset;
    // Started once, deliberately. Re-running whenever the library object
    // changed identity would restart the scan under the person reading it.
  }, []);

  /*
   * Only the true copies are ticked in advance — never a member of a moment,
   * however confident the number looks. Suggesting the deletion of a
   * photograph somebody chose to keep is the one failure this feature cannot
   * afford.
   */
  useEffect(() => {
    if (state.phase !== "done") return;
    const suggested = new Set<string>();
    for (const set of state.sets) {
      // A set already settled is not pre-ticked again, or "keep all" would be
      // undone by the next scan.
      if (reviewed.has(setSignature(set.ids))) continue;
      for (const id of set.redundant) suggested.add(id);
    }
    setMarked(suggested);
  }, [state.phase, state.sets, reviewed]);

  const toggle = useCallback((id: string) => {
    setMarked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /*
   * Sets somebody has already settled are hidden, not deleted.
   *
   * The scan is deterministic, so without this it finds the same six near
   * identical selfies every visit and asks about them again, having been told.
   * They can still be brought back — a decision is not a promise never to
   * change your mind.
   */
  const visible = useMemo(
    () =>
      state.sets.filter(
        (set) => showReviewed || !reviewed.has(setSignature(set.ids)),
      ),
    [state.sets, reviewed, showReviewed],
  );
  const hiddenCount = state.sets.length - visible.length;

  const keepAll = useCallback(
    (set: RelatedSet) => {
      setReviewed((current) => {
        const next = new Set(current);
        next.add(setSignature(set.ids));
        saveReviewed(groupId, next);
        return next;
      });
      // Anything ticked in a set being kept is no longer ticked, or the delete
      // button would still be counting photographs somebody just kept.
      setMarked((current) => {
        const next = new Set(current);
        for (const id of set.ids) next.delete(id);
        return next;
      });
    },
    [groupId],
  );

  const copies = useMemo(
    () => visible.filter((set) => set.relation === "duplicate"),
    [visible],
  );
  const moments = useMemo(
    () => visible.filter((set) => set.relation === "similar"),
    [visible],
  );
  const savings = useMemo(() => reclaimable(state.sets), [state.sets]);
  const progress =
    state.total === 0 ? 0 : Math.round((state.hashed / state.total) * 100);

  const summary = () => {
    if (state.phase === "listing") return "Reading your library…";
    if (state.phase === "hashing") {
      return `Comparing ${state.hashed.toLocaleString()} of ${state.total.toLocaleString()}…`;
    }
    if (state.sets.length === 0) {
      return `Nothing looks repeated. Compared ${state.total.toLocaleString()} photos.`;
    }
    const parts: string[] = [];
    if (copies.length > 0) {
      parts.push(`${savings} extra ${savings === 1 ? "copy" : "copies"}`);
    }
    if (moments.length > 0) {
      parts.push(
        `${moments.length} ${moments.length === 1 ? "moment" : "moments"} to look at`,
      );
    }
    return parts.join(" · ");
  };

  return createPortal(
    <>
      <div
        className="fc-dupes"
        role="dialog"
        aria-modal="true"
        aria-label="Repeated photos"
      >
        <header className="fc-dupes__bar">
          <div>
            <h2>Repeated photos</h2>
            <p>{summary()}</p>
          </div>

          <div className="fc-dupes__actions">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            {state.phase === "done" && canDelete && (
              <Button
                variant="danger"
                disabled={marked.size === 0}
                onClick={() => onDelete([...marked])}
              >
                Move {marked.size} to trash
              </Button>
            )}
          </div>
        </header>

        {state.phase !== "done" && (
          <div
            className="fc-dupes__progress"
            role="progressbar"
            aria-valuenow={progress}
          >
            <span style={{ transform: `scaleX(${progress / 100})` }} />
          </div>
        )}

        <div className="fc-dupes__body">
          {state.phase !== "done" && (
            <p className="fc-dupes__note">
              Every photo is compared on this device — the server cannot see
              them.
            </p>
          )}

          {state.phase === "done" && state.unreadable > 0 && (
            <p className="fc-dupes__note fc-dupes__note--warn">
              {state.unreadable} {state.unreadable === 1 ? "photo" : "photos"}{" "}
              could not be read and
              {state.unreadable === 1 ? " was" : " were"} left out.
            </p>
          )}

          {state.phase === "done" && hiddenCount > 0 && (
            <p className="fc-dupes__note">
              {hiddenCount} {hiddenCount === 1 ? "set" : "sets"} you already
              settled {hiddenCount === 1 ? "is" : "are"} hidden.{" "}
              <button
                type="button"
                className="fc-dupes__link"
                onClick={() => setShowReviewed((value) => !value)}
              >
                {showReviewed ? "Hide them again" : "Show them"}
              </button>
            </p>
          )}

          {state.phase === "done" &&
            visible.length === 0 &&
            state.sets.length > 0 && (
              <p className="fc-dupes__note">
                Everything found has already been settled.
              </p>
            )}

          {copies.length > 0 && (
            <>
              <h3 className="fc-dupes__heading">
                Copies
                <span>
                  The same photo more than once. The extras are already ticked.
                </span>
              </h3>
              {copies.map((set) => (
                <SetRow
                  key={set.ids[0]}
                  set={set}
                  groupId={groupId}
                  thumbRefs={thumbRefs}
                  marked={marked}
                  onToggle={toggle}
                  onCompare={() => setComparing(set)}
                  onKeepAll={() => keepAll(set)}
                />
              ))}
            </>
          )}

          {moments.length > 0 && (
            <>
              <h3 className="fc-dupes__heading">
                One moment, several shots
                <span>
                  Different pictures of the same thing. Nothing is ticked — open
                  one to see them side by side and decide, or keep them all.
                </span>
              </h3>
              {moments.map((set) => (
                <SetRow
                  key={set.ids[0]}
                  set={set}
                  groupId={groupId}
                  thumbRefs={thumbRefs}
                  marked={marked}
                  onToggle={toggle}
                  onCompare={() => setComparing(set)}
                  onKeepAll={() => keepAll(set)}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {comparing && (
        <CompareView
          groupId={groupId}
          set={comparing}
          marked={marked}
          onToggle={toggle}
          onClose={() => setComparing(null)}
        />
      )}
    </>,
    document.body,
  );
}

function SetRow({
  set,
  groupId,
  thumbRefs,
  marked,
  onToggle,
  onCompare,
  onKeepAll,
}: {
  set: RelatedSet;
  groupId: string;
  thumbRefs: ReadonlyMap<string, MediaRef>;
  marked: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onCompare: () => void;
  onKeepAll: () => void;
}) {
  return (
    <section className="fc-dupes__group">
      <div className="fc-dupes__group-head">
        <h4>
          {set.ids.length} photos
          <span className="fc-dupes__confidence">
            {set.relation === "duplicate"
              ? set.spread <= IDENTICAL_WITHIN
                ? "Practically identical"
                : "Near-identical"
              : "Same moment"}
          </span>
        </h4>
        <div className="fc-dupes__group-actions">
          <Button variant="ghost" size="sm" onClick={onCompare}>
            Compare
          </Button>
          {/*
            The answer the scan could not take before. Settling a set is the
            common outcome for a burst — they are all worth keeping — and
            without somewhere to say so the same six come back every visit.
          */}
          <Button variant="ghost" size="sm" onClick={onKeepAll}>
            Keep all
          </Button>
        </div>
      </div>

      <div className="fc-dupes__row">
        {set.ids.map((id, position) => (
          <Candidate
            key={id}
            groupId={groupId}
            source={thumbRefs.get(id) ?? null}
            oldest={position === 0}
            marked={marked.has(id)}
            onToggle={() => onToggle(id)}
          />
        ))}
      </div>
    </section>
  );
}

function Candidate({
  groupId,
  source,
  oldest,
  marked,
  onToggle,
}: {
  groupId: string;
  source: MediaRef | null;
  oldest: boolean;
  marked: boolean;
  onToggle: () => void;
}) {
  const { url } = useDecryptedUrl(source, groupId);

  return (
    <button
      type="button"
      className="fc-dupes__candidate"
      data-marked={marked || undefined}
      aria-pressed={marked}
      aria-label={marked ? "Keep this one instead" : "Move this one to trash"}
      onClick={onToggle}
    >
      {url ? <img src={url} alt="" /> : <span className="fc-dupes__blank" />}
      <span className="fc-dupes__tag">
        {marked ? "To trash" : oldest ? "Oldest" : "Keeping"}
      </span>
    </button>
  );
}
